import api from './api';
import { authService } from './authService';
import { uploadImageToCloudinary } from './cloudinaryService';

async function prepareProductFormDataWithOptionalCloudinary(formData) {
  const file = formData.get('image');
  const hasFile = file && typeof file !== 'string' && file.size != null;
  if (!hasFile) return formData;

  try {
    const secureUrl = await uploadImageToCloudinary(file, 'menu_product');
    const next = new FormData();
    for (const [key, value] of formData.entries()) {
      if (key === 'image') continue;
      next.append(key, value);
    }
    next.append('imageUrl', secureUrl);
    return next;
  } catch (e) {
    console.warn('Cloudinary ürün görseli atlandı, sunucu upload:', e?.message);
    return formData;
  }
}

export const menuService = {
  getCategories: async () => {
    try {
      const restaurantId = authService.getCurrentRestaurantId();
      const response = await api.get(`/api/business/menu/categories?restaurantId=${restaurantId}`);
      return response.data;
    } catch (error) {
      console.error('Get categories error:', error);
      throw error;
    }
  },

  addCategory: async (formData) => {
    try {
      const restaurantId = authService.getCurrentRestaurantId();
      const response = await api.post('/api/business/menu/categories', {
        name: formData.name,
        restaurantId: restaurantId
      });
      return response.data;
    } catch (error) {
      console.error('Add category error:', error);
      throw error;
    }
  },

  deleteCategory: async (categoryId) => {
    try {
      const response = await api.delete(`/api/business/menu/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('Delete category error:', error);
      throw error;
    }
  },

  getProducts: async () => {
    try {
      const restaurantId = authService.getCurrentRestaurantId();
      const response = await api.get(`/api/business/menu/products?restaurantId=${restaurantId}`);
      return response.data;
    } catch (error) {
      console.error('Get products error:', error);
      throw error;
    }
  },

  addProduct: async (formData) => {
    try {
      const prepared = await prepareProductFormDataWithOptionalCloudinary(formData);
      const response = await api.post('/api/business/menu/products', prepared, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Add product error:', error);
      throw error;
    }
  },

  deleteProduct: async (productId) => {
    try {
      const response = await api.delete(`/api/business/menu/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Delete product error:', error);
      throw error;
    }
  },

  updateProductStatus: async (productId, isActive) => {
    try {
      const response = await api.patch(`/api/business/menu/products/${productId}/status`, {
        isActive
      });
      return response.data;
    } catch (error) {
      console.error('Update product status error:', error);
      throw error;
    }
  },

  updateProductFeatured: async (productId, isFeatured) => {
    try {
      const response = await api.patch(`/api/business/menu/products/${productId}/featured`, {
        isFeatured
      });
      return response.data;
    } catch (error) {
      console.error('Update product featured error:', error);
      throw error;
    }
  },

  updateProduct: async (productId, formData) => {
    try {
      const prepared = await prepareProductFormDataWithOptionalCloudinary(formData);
      const response = await api.put(`/api/business/menu/products/${productId}`, prepared, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Update product error:', error);
      throw error;
    }
  },
  
  updateProductDiscount: async (productId, discountData) => {
    try {
      const formData = new FormData();
      
      // productId discountData'da varsa onu kullan (genel buton için)
      const finalProductId = discountData.productId || productId;
      
      // Boş string'leri de gönder (indirim kaldırma için)
      // FormData'da boş string göndermek null'a dönüşür, bu yüzden açıkça gönder
      if (discountData.discountPrice !== undefined) {
        formData.append('discountPrice', discountData.discountPrice === '' || discountData.discountPrice === null ? '' : String(discountData.discountPrice));
      }
      if (discountData.discountType !== undefined) {
        formData.append('discountType', discountData.discountType === '' || discountData.discountType === null ? '' : String(discountData.discountType));
      }
      if (discountData.discountValue !== undefined) {
        formData.append('discountValue', discountData.discountValue === '' || discountData.discountValue === null ? '' : String(discountData.discountValue));
      }
      if (discountData.discountStartDate !== undefined) {
        formData.append('discountStartDate', discountData.discountStartDate === '' || discountData.discountStartDate === null ? '' : String(discountData.discountStartDate));
      }
      if (discountData.discountEndDate !== undefined) {
        formData.append('discountEndDate', discountData.discountEndDate === '' || discountData.discountEndDate === null ? '' : String(discountData.discountEndDate));
      }
      
      const response = await api.put(`/api/business/menu/products/${finalProductId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Update product discount error:', error);
      throw error;
    }
  },
  
  // Menü performans analizi verilerini getir
  getMenuAnalytics: async () => {
    try {
      const restaurantId = authService.getCurrentRestaurantId();
      
      // Menü performans endpoint'ini kullan
      const response = await api.get(`/api/business/menu/performance?restaurantId=${restaurantId}`);
      
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      throw new Error('Veri formatı geçersiz');
    } catch (error) {
      console.error('Get menu analytics error:', error);
      
      // Hata durumunda ürünleri getir ve sipariş sayılarını kullan
      const restaurantId = authService.getCurrentRestaurantId();
      const productsResponse = await api.get(`/api/business/menu/products?restaurantId=${restaurantId}`);
      const products = productsResponse.data.data || [];
      
      
      // Ürünleri sipariş sayısına göre sırala
      const sortedProducts = [...products]
        .sort((a, b) => (b.orderCount || 0) - (a.orderCount || 0));
      
      // En çok satanları al
      const topSellingItems = sortedProducts
        .filter(product => (product.orderCount || 0) > 0)
        .slice(0, 5)
        .map(product => ({
          name: product.name,
          orderCount: product.orderCount || 0,
          price: product.price
        }));
      
      // Kategori bazlı performans
      const categoriesMap = {};
      products.forEach(product => {
        const categoryName = product.categoryName || 'Diğer';
        if (!categoriesMap[categoryName]) {
          categoriesMap[categoryName] = 0;
        }
        categoriesMap[categoryName] += (product.orderCount || 0);
      });
      
      const categoriesPerformance = Object.entries(categoriesMap)
        .map(([name, orderCount]) => ({ name, orderCount }))
        .filter(category => category.orderCount > 0)
        .sort((a, b) => b.orderCount - a.orderCount);
      
      return {
        totalItems: products.length,
        topSellingItems,
        categoriesPerformance
      };
    }
  },

  // Ürün sipariş sayısını kontrol et
  checkOrderCounts: async () => {
    try {
      const restaurantId = authService.getCurrentRestaurantId();
      const response = await api.get(`/api/business/menu/products?restaurantId=${restaurantId}`);
      
      if (response.data && response.data.success) {
        const products = response.data.data || [];
        
        // Sipariş sayılarını kontrol et
        const hasOrderCounts = products.some(product => product.orderCount > 0);
        
        return hasOrderCounts;
      }
      
      return false;
    } catch (error) {
      console.error('Sipariş sayısı kontrolü hatası:', error);
      return false;
    }
  }
}; 